import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Overline, StatusPill } from "@/components/common";
import IntelligenceCard from "@/components/IntelligenceCard";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Flag, MapPin, Check, Navigation } from "lucide-react";

const mins = (m) => `${Math.floor(m / 60) ? Math.floor(m / 60) + "h " : ""}${m % 60}m`;

function HoleNode({ h }) {
  const color = h.state === "Major Bottleneck" ? "#DC2626" : h.state === "Minor Delay" ? "#D97706" : "#059669";
  return (
    <div
      data-testid={`hole-${h.hole}`}
      className="relative rounded-xl border bg-white p-3 card-hover"
      style={{ borderColor: `${color}40` }}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono2 text-xs text-[#A1A1AA]">HOLE</span>
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      </div>
      <div className="font-display text-2xl font-semibold text-[#1C1F1E] mt-0.5">{h.hole}</div>
      <div className="mt-2 space-y-0.5 text-xs">
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Groups</span><span className="font-mono2 text-[#1C1F1E]">{h.groups_on_hole}</span></div>
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Target</span><span className="font-mono2 text-[#52525B]">{h.target_time}m</span></div>
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Actual</span><span className="font-mono2 text-[#1C1F1E]">{h.actual_time}m</span></div>
      </div>
      <div className="mt-2 pt-2 border-t border-[#F1F3F2] flex items-center justify-between">
        <span className="text-xs text-[#A1A1AA]">Delay</span>
        <span className="font-mono2 text-xs font-semibold" style={{ color: h.delay > 0 ? color : "#059669" }}>
          {h.delay > 0 ? `+${h.delay}m` : "On pace"}
        </span>
      </div>
    </div>
  );
}

export default function Pace() {
  const [data, setData] = useState(null);
  const load = () => api.get("/pace").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const dispatch = async () => {
    await api.post("/pace/dispatch-marshal");
    toast.success("Marshal dispatched to Hole 7", { description: "Crew notified · ETA 4 min" });
    load();
  };

  if (!data) return <div className="h-96 animate-pulse bg-white border border-[#E5E7EB] rounded-xl" />;
  const M = data.metrics;

  const metrics = [
    { l: "Average Round", v: M.avg_round, danger: true },
    { l: "Target", v: M.target },
    { l: "Groups On Course", v: M.groups_on_course },
    { l: "Groups Behind Pace", v: M.groups_behind, danger: true },
    { l: "Largest Bottleneck", v: M.largest_bottleneck, danger: true },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E]">Pace of Play</h1>
        <p className="text-[#52525B] mt-1">Live course flow across all 18 holes.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {metrics.map((m) => (
          <Card key={m.l} className="p-5">
            <Overline>{m.l}</Overline>
            <div className={cn("font-display text-2xl font-semibold font-mono2 mt-2", m.danger ? "text-[#DC2626]" : "text-[#1C1F1E]")}>{m.v}</div>
          </Card>
        ))}
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-5">
          <Overline>18-Hole Course Flow</Overline>
          <div className="flex items-center gap-4 text-xs">
            {[["On Pace", "#059669"], ["Minor Delay", "#D97706"], ["Major Bottleneck", "#DC2626"]].map(([l, c]) => (
              <span key={l} className="flex items-center gap-1.5 text-[#52525B]"><span className="w-2 h-2 rounded-full" style={{ background: c }} />{l}</span>
            ))}
          </div>
        </div>
        <div className="mb-2 overline text-[#A1A1AA]">Front Nine</div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3 mb-6">
          {data.holes.slice(0, 9).map((h) => <HoleNode key={h.hole} h={h} />)}
        </div>
        <div className="mb-2 overline text-[#A1A1AA]">Back Nine</div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
          {data.holes.slice(9).map((h) => <HoleNode key={h.hole} h={h} />)}
        </div>
      </Card>

      <Card className="p-6 border-[#DC2626]/20 bg-[#DC2626]/[0.02]">
        <div className="flex items-start gap-4">
          <div className="w-9 h-9 rounded-lg bg-[#DC2626]/10 border border-[#DC2626]/20 flex items-center justify-center shrink-0"><MapPin size={17} className="text-[#DC2626]" /></div>
          <div className="flex-1">
            <Overline className="!text-[#DC2626]">{data.intelligence.title}</Overline>
            <p className="text-[15px] text-[#1C1F1E] mt-1.5 leading-relaxed">{data.intelligence.body}</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><Overline>Recommended Action</Overline><p className="text-sm font-medium text-[#1C1F1E] mt-1">{data.intelligence.action}</p></div>
              <div><Overline>Expected Impact</Overline><p className="text-sm font-semibold text-[#0F3F2D] mt-1">{data.intelligence.impact}</p></div>
            </div>
            <div className="mt-4">
              {data.marshal_dispatched ? (
                <span data-testid="marshal-dispatched" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#059669]/10 text-[#059669] text-sm font-medium border border-[#059669]/20"><Check size={15} />Marshal Dispatched to Hole 7</span>
              ) : (
                <button data-testid="dispatch-marshal-btn" onClick={dispatch} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F3F2D] hover:bg-[#165c42] text-white text-sm font-medium transition-colors active:scale-95"><Flag size={15} />Dispatch Marshal to Hole 7</button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div>
        <Overline>Active Bottlenecks</Overline>
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          {data.bottlenecks?.map((b, i) => (
            <Card key={i} className="p-5 border-[#DC2626]/20 bg-[#DC2626]/[0.02]" data-testid={`bottleneck-${i}`}>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#DC2626]"><MapPin size={13} />Pace Bottleneck</span>
                <StatusPill status="Critical" />
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-mono2 font-semibold text-[#1C1F1E]">{b.tee_time}</span>
                <span className="text-sm text-[#52525B]">· Hole {b.hole}</span>
                <span className="font-mono2 font-semibold text-[#DC2626] ml-auto">+{b.minutes_behind} min</span>
              </div>
              <p className="text-sm text-[#52525B] mt-1">{b.downstream_affected} downstream groups affected.</p>
              <div className="mt-3 grid grid-cols-1 gap-2">
                <div><Overline>Recommended Action</Overline><p className="text-sm font-medium text-[#1C1F1E] mt-0.5">{b.action}</p></div>
                <div><Overline>Estimated Impact</Overline><p className="text-sm font-semibold text-[#0F3F2D] mt-0.5">{b.impact}</p></div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Card className="p-6 border-dashed" data-testid="gps-map-placeholder">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Navigation size={16} className="text-[#0F3F2D]" /><Overline>Live GPS Course Map</Overline></div>
          <span className="text-xs font-medium text-[#A1A1AA] bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5 rounded-full">Coming Soon</span>
        </div>
        <div className="mt-4 relative rounded-xl overflow-hidden h-56 bg-[#16201B]">
          <div className="absolute inset-0 opacity-[0.14]" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1605144884374-ecbb643615f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Navigation size={26} className="text-white/70" />
            <p className="text-white/80 text-sm mt-3 max-w-sm">GPS-enabled carts will appear here in real time, color-coded by pace status — On Pace, Watch, Behind, Critical.</p>
            <div className="flex items-center gap-4 mt-4">
              {data.severity_states?.map((s) => (
                <span key={s} className="flex items-center gap-1.5 text-xs text-white/70"><span className="w-2 h-2 rounded-full" style={{ background: s === "On Pace" ? "#059669" : s === "Watch" ? "#D97706" : "#DC2626" }} />{s}</span>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div>
        <div className="flex items-center justify-between">
          <Overline>Live Group Tracking</Overline>
          {data.estimated_round_finish && <span className="text-xs text-[#52525B]">Est. last group finish · <span className="font-mono2 text-[#1C1F1E]">{data.estimated_round_finish}</span></span>}
        </div>
        <Card className="mt-3 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E7EB] text-left">
                {["Group", "Current Hole", "Elapsed Time", "Target Time", "Delay", "Est. Finish", "Status"].map((h) => <th key={h} className="px-5 py-3 overline font-semibold text-[#71717A]">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.groups.map((g) => (
                  <tr key={g.id} data-testid={`group-row`} className="border-b border-[#F1F3F2] last:border-0 hover:bg-[#0F3F2D]/[0.02] transition-colors">
                    <td className="px-5 py-2.5 font-mono2 font-medium text-[#1C1F1E]">{g.group}</td>
                    <td className="px-5 py-2.5 font-mono2 text-[#52525B]">Hole {g.current_hole}</td>
                    <td className="px-5 py-2.5 font-mono2 text-[#52525B]">{mins(g.elapsed)}</td>
                    <td className="px-5 py-2.5 font-mono2 text-[#A1A1AA]">{mins(g.target)}</td>
                    <td className="px-5 py-2.5 font-mono2 font-semibold" style={{ color: g.delay >= 10 ? "#DC2626" : g.delay >= 6 ? "#DC2626" : g.delay >= 3 ? "#D97706" : "#059669" }}>{g.delay > 0 ? `+${g.delay}m` : `${g.delay}m`}</td>
                    <td className="px-5 py-2.5 font-mono2 text-[#52525B]">{g.estimated_finish}</td>
                    <td className="px-5 py-2.5"><StatusPill status={g.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
